import { type IMediator } from '#/core/mediator';

import { getCssVarValueByName } from '#/shared/utils/get-css-var-value-by-name';

import { SERVICES_TYPES } from '#/services-types';

import { DeviceType, type IDeviceService } from '../device';
import { type ILogService } from '../log';
import { type IUrlParamsService, UrlParamsName } from '../url-params';

import { APP_MODES, APP_THEMES, PALETTE_ROLES } from './theme.model';
import { type AppMode, type AppTheme, type IThemeService, type Palette } from './theme.types';

export class ThemeService implements IThemeService {
  readonly #serviceName = 'ThemeService';

  get #urlParamsService() {
    return this._mediator.get<IUrlParamsService>(SERVICES_TYPES.UrlParamsService);
  }
  get #deviceService() {
    return this._mediator.get<IDeviceService>(SERVICES_TYPES.DeviceService);
  }
  get #logService() {
    return this._mediator.get<ILogService>(SERVICES_TYPES.LogService);
  }

  #theme: AppTheme = 'light';

  #mode: AppMode = 'default';

  readonly #onChangeThemeCallbacks: ((theme: AppTheme) => void)[] = [];

  get #providedTheme(): AppTheme | undefined {
    const providedTheme = this.#urlParamsService.getParamByName(UrlParamsName.Theme);

    if (!providedTheme) return;

    return APP_THEMES.includes(providedTheme as AppTheme) ? (providedTheme as AppTheme) : undefined;
  }

  get #providedMode(): AppMode | undefined {
    const providedMode = this.#urlParamsService.getParamByName(UrlParamsName.Mode);

    if (!providedMode) return;

    return APP_MODES.includes(providedMode as AppMode) ? (providedMode as AppMode) : undefined;
  }

  readonly #darkThemeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

  get #browserTheme(): AppTheme {
    return this.#darkThemeMediaQuery.matches ? 'dark' : 'light';
  }

  constructor(private readonly _mediator: IMediator) {
    // Disable automatic theme change in iframe
    if (this.#deviceService.deviceType !== DeviceType.Iframe) {
      this.#darkThemeMediaQuery.addEventListener('change', ({ matches }: MediaQueryListEvent) => {
        this.#changeTheme(matches ? 'dark' : 'light');
      });
    }

    this.#logService.info(this.#serviceName, 'Instance was created.');

    this.#applyMode(this.#providedMode || 'default');
    this.#applyTheme(this.#providedTheme || this.#browserTheme);
  }

  get palette(): Palette {
    return Object.fromEntries(PALETTE_ROLES.map((role) => [role, getCssVarValueByName(`--${role}`)])) as Palette;
  }

  get theme() {
    return this.#theme;
  }

  get mode() {
    return this.#mode;
  }

  get isDarkTheme(): boolean {
    return this.#theme === 'dark';
  }

  onThemeChange(cb: (theme: AppTheme) => void) {
    this.#onChangeThemeCallbacks.push(cb);
  }

  // Transition to another theme and tell the subscribers. #applyTheme alone does not notify, so the
  // constructor can apply the initial theme without firing callbacks nobody has registered yet.
  #changeTheme(newTheme: AppTheme) {
    if (newTheme === this.#theme) return;

    this.#applyTheme(newTheme);

    this.#onChangeThemeCallbacks.forEach((cb) => cb(newTheme));
  }

  // ? The two attributes are the whole switch: public/index.css declares each role once, overrides the
  // ? raw colors per [data-theme] and the role assignments per [data-mode], so the cascade resolves
  // ? every var(--role) for the active pair.
  #applyTheme(theme: AppTheme) {
    this.#logService.info(this.#serviceName, 'Set new theme:', theme);

    this.#theme = theme;

    document.documentElement.dataset.theme = theme;

    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', getCssVarValueByName('--surface'));

    // ? Written back because the theme can still change at runtime; the mode cannot, so it is not.
    this.#urlParamsService.setParamByName<AppTheme>(UrlParamsName.Theme, theme);
  }

  #applyMode(mode: AppMode) {
    this.#logService.info(this.#serviceName, 'Set mode:', mode);

    this.#mode = mode;

    document.documentElement.dataset.mode = mode;
  }
}
